import { action, computed, makeObservable, observable } from "mobx";
import { BaseStore } from "stores/BaseStore";
import {
    COMPACT_CHART_COLUMNS,
    COMPACT_CHART_WIDTH_PX,
} from "../../../Dashboard/Layout/layoutConfig";

/**
 * Per-chart-card sub-store that owns the chart's measured width and the
 * layout choices that follow from it: whether the chart is compact, and how
 * much of its width goes to axis labels.
 */
export default class ChartWidthStore extends BaseStore {
    /** The plot's rendered width in pixels, as last measured. */
    @observable measuredWidthPx = 0;

    /** The card's width in grid columns, when it sits on the dashboard grid. */
    @observable layoutColumns: number | undefined = undefined;

    constructor() {
        super("Chart Width Store");
        makeObservable(this);
    }

    @action
    setMeasuredWidth = (widthPx: number) => {
        if (!Number.isFinite(widthPx) || widthPx < 0) return;
        const rounded = Math.round(widthPx);
        if (this.measuredWidthPx === rounded) return;
        this.measuredWidthPx = rounded;
    };

    @action
    setLayoutColumns = (columns: number | undefined) => {
        this.layoutColumns = columns;
    };

    @computed
    get isCompact(): boolean {
        return (
            (this.layoutColumns !== undefined &&
                this.layoutColumns <= COMPACT_CHART_COLUMNS) ||
            (this.measuredWidthPx > 0 &&
                this.measuredWidthPx < COMPACT_CHART_WIDTH_PX)
        );
    }

    /**
     * Horizontal bars carry category names on the left axis, so they give
     * labels the most room when compact.
     */
    @computed
    get horizontalBarChartAreaLeftOffset(): string {
        return this.isCompact ? "26%" : "15%";
    }

    @computed
    get horizontalBarChartAreaWidth(): string {
        return this.isCompact ? "70%" : "85%";
    }
}
